import React from "react";
import { Box, Typography, IconButton, useTheme } from "@mui/material";
import { AlertSchedule, DailyTimeRange, DayOfWeekEnum } from "../../../API";
import AddCircleOutlineIcon from "../icons/AddCircleOutlineIcon";
import RemoveCircleOutlineIcon from "../icons/RemoveCircleOutlineIcon";
import { TimeRangeRow } from "./TimeRangeRow";

export type DaySchedule = Omit<AlertSchedule, "__typename">;

type TimeRange = Omit<DailyTimeRange, "__typename">;

export interface AlertHoursPickerProps {
  value: DaySchedule[];
  onChange: (next: DaySchedule[]) => void;
  activeDays: DayOfWeekEnum[];
  stepMinutes?: 15 | 30 | 60;
  startOfWeek?: DayOfWeekEnum.SUNDAY | DayOfWeekEnum.MONDAY;
  label?: string;
}

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR;

const DEFAULT_STEP_MINUTES = 30;

export const DEFAULT_PERIOD_MINUTES = 1440;

const clamp = (n: number, min: number, max: number) =>
  Math.min(Math.max(n, min), max);

export function formatLastDayMinute(value: number) {
  return value === DEFAULT_PERIOD_MINUTES ? value - 1 : value;
}

const formatLabel12hFromMin = (totalMin: number) => {
  const h24 = Math.floor(totalMin / MINUTES_PER_HOUR);
  const m = totalMin % MINUTES_PER_HOUR;
  const ampm = h24 >= 12 ? "pm" : "am";
  const h12 = ((h24 + 11) % 12) + 1;

  return `${h12}:${m.toString().padStart(2, "0")}${ampm}`;
};

const buildOptions = (stepMinutes: number) => {
  const opts: { label: string; value: number }[] = [];

  for (let m = 0; m <= MINUTES_PER_DAY; m += stepMinutes) {
    opts.push({ value: m, label: formatLabel12hFromMin(m % MINUTES_PER_DAY) });
  }

  return opts;
};

function normalize(
  value: DaySchedule[],
  startOfWeek: DayOfWeekEnum.SUNDAY | DayOfWeekEnum.MONDAY
) {
  const by = new Map<DayOfWeekEnum, DaySchedule>();

  let ORDER: DayOfWeekEnum[] = [
    DayOfWeekEnum.SUNDAY,
    DayOfWeekEnum.MONDAY,
    DayOfWeekEnum.TUESDAY,
    DayOfWeekEnum.WEDNESDAY,
    DayOfWeekEnum.THURSDAY,
    DayOfWeekEnum.FRIDAY,
    DayOfWeekEnum.SATURDAY,
  ];

  if (startOfWeek === DayOfWeekEnum.MONDAY) {
    ORDER = ORDER.filter(d => d !== DayOfWeekEnum.SUNDAY);

    ORDER.push(DayOfWeekEnum.SUNDAY);
  }

  value?.forEach(d =>
    by.set(d.dayOfWeek, {
      dayOfWeek: d.dayOfWeek,
      dailyTimeRange: [...(d.dailyTimeRange || [])],
    })
  );

  return ORDER.map(
    dow => by.get(dow) ?? { dayOfWeek: dow, dailyTimeRange: [] }
  );
}

export const AlertHoursPicker: React.FC<AlertHoursPickerProps> = ({
  value,
  onChange,
  activeDays = [],
  stepMinutes = DEFAULT_STEP_MINUTES,
  startOfWeek = DayOfWeekEnum.SUNDAY,
  label = "Alert hours",
}) => {
  const theme = useTheme();
  const options = React.useMemo(() => buildOptions(stepMinutes), [stepMinutes]);

  const data = React.useMemo(
    () => normalize(value ?? [], startOfWeek),
    [value, startOfWeek]
  );

  const updateDay = (dayIdx: number, nextDay: DaySchedule) => {
    const next = [...data];

    next[dayIdx] = nextDay;

    onChange(next);
  };

  const ensureValidRange = (r: TimeRange): TimeRange => {
    if (r.startMinutesFromMidnight >= r.endMinutesFromMidnight) {
      const end = clamp(
        r.startMinutesFromMidnight + stepMinutes,
        0,
        MINUTES_PER_DAY
      );

      return {
        startMinutesFromMidnight: r.startMinutesFromMidnight,
        endMinutesFromMidnight: end,
      };
    }

    return r;
  };

  const addPeriod = (dayIdx: number) => {
    const day = data[dayIdx];

    const nextPeriods = [
      ...day.dailyTimeRange,
      ensureValidRange({
        startMinutesFromMidnight: 0,
        endMinutesFromMidnight: DEFAULT_PERIOD_MINUTES,
      }),
    ];

    updateDay(dayIdx, {
      ...day,
      dailyTimeRange: nextPeriods as DailyTimeRange[],
    });
  };

  const removePeriod = (dayIdx: number, pIdx: number) => {
    const day = data[dayIdx];
    const nextPeriods = day.dailyTimeRange.filter((_, i) => i !== pIdx);

    updateDay(dayIdx, { ...day, dailyTimeRange: nextPeriods });
  };

  const labelColorDisabled = theme.palette.text.disabled;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "120px 1fr auto",
        gap: 1.5,
        alignItems: "start",
      }}
    >
      <Box />
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
      >
        {label}
      </Typography>
      <Box />

      {data.map((day, dayIdx) => {
        const isActive =
          day.dailyTimeRange.length > 0 && activeDays.includes(day.dayOfWeek);

        const disabled =
          !activeDays.includes(day.dayOfWeek) && day.dailyTimeRange.length > 0;

        return (
          <React.Fragment key={day.dayOfWeek}>
            <Box
              sx={{
                height: "100%",
                textTransform: "lowercase",
                "&::first-letter": { textTransform: "uppercase" },
              }}
            >
              <Typography
                variant="body2Regular"
                sx={{
                  color: isActive
                    ? theme.palette.text.primary
                    : labelColorDisabled,
                }}
              >
                {day.dayOfWeek}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                rowGap: 1,
                opacity: disabled ? 0.6 : 1,
              }}
            >
              {day.dailyTimeRange.map((p, pIdx) => {
                const onChangeHandler = ({
                  start,
                  end,
                }: {
                  start: number;
                  end: number;
                }) => {
                  const next = [...day.dailyTimeRange];

                  next[pIdx] = {
                    startMinutesFromMidnight: start,
                    endMinutesFromMidnight: end,
                    __typename: "DailyTimeRange",
                  };

                  updateDay(dayIdx, {
                    ...day,
                    dailyTimeRange: next,
                  });
                };

                return (
                  <TimeRangeRow
                    key={pIdx}
                    start={p.startMinutesFromMidnight}
                    end={p.endMinutesFromMidnight}
                    options={options}
                    disabled={disabled}
                    renderValue={formatLabel12hFromMin}
                    onChange={onChangeHandler}
                  />
                );
              })}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                height: "100%",
                justifyContent: "flex-start",
              }}
            >
              {day.dailyTimeRange.map((p, pIdx) => (
                <Box
                  sx={{
                    height: "100%",
                  }}
                  key={pIdx}
                >
                  <Typography variant="overline" component="span">
                    <IconButton
                      sx={{ height: "100%" }}
                      size="small"
                      onClick={() => addPeriod(dayIdx)}
                      disabled={disabled}
                    >
                      <AddCircleOutlineIcon disabled={disabled} />
                    </IconButton>
                  </Typography>

                  <Typography
                    sx={{
                      visibility:
                        day.dailyTimeRange.length > 1 ? "visible" : "hidden",
                    }}
                    variant="overline"
                    component="span"
                  >
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => removePeriod(dayIdx, pIdx)}
                    >
                      <RemoveCircleOutlineIcon disabled={disabled} />
                    </IconButton>
                  </Typography>
                </Box>
              ))}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};
